@if (!recordId()) {
    <p class="m-1 text-sm text-gray-600 dark:text-gray-400" data-testid="entity-translations-no-record">
        {{ 'entity-translations.save-record-first' | translate }}
    </p>
} @else if (loading()) {
    <p class="m-1 text-sm text-gray-600 dark:text-gray-400">{{ 'common.loading' | translate }}</p>
} @else if (!row()) {
    <p class="m-1 text-sm text-gray-600 dark:text-gray-400">
        {{ 'entity-translations.not-translatable' | translate }}
    </p>
} @else {
    <div class="w-full" data-testid="entity-translations-widget">
        <label class="h6 label-with-hr text-primary font-semibold block mb-0">
            {{ 'entity-translations.title' | translate }}
        </label>
        <hr class="border-gray-200 !opacity-50 mb-3" />

        <div class="mb-1.5 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
            <!-- Fixed width only for the dropdown: phx-select's host grows to fill any flex row it sits in directly. -->
            <div class="flex shrink-0 flex-col gap-1" [ngClass]="useTargetDropdown() ? 'w-64' : ''">
                <label class="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {{ 'entity-translations.target' | translate }}
                </label>

                @if (useTargetDropdown()) {
                    <phx-select
                        class="w-full"
                        size="small"
                        dropdownType="EntityTranslationsTargetLanguage"
                        [items]="languageOptions()"
                        optionLabel="label"
                        optionValue="code"
                        [value]="targetLanguage()"
                        (onModelChange)="onTargetLanguageChange($event)"
                        [showClear]="false"
                        [searchable]="true"
                        [itemTemplate]="languageOptionTemplate"
                        [dataTestId]="'entity-translations-target-language'"
                    />
                } @else {
                    <p-selectButton
                        [options]="languageOptions()"
                        [ngModel]="targetLanguage()"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="onTargetLanguageChange($event)"
                        optionValue="code"
                        [allowEmpty]="false"
                        data-testid="entity-translations-target-language"
                    >
                        <ng-template #item let-option>
                            <span class="flex items-center gap-2" [pTooltip]="option.label + ' - ' + option.coverageLabel">
                                <span class="h-2 w-2 shrink-0 rounded-full" [ngClass]="coverageDotClass(option.coverage)"></span>
                                <span class="whitespace-nowrap">
                                    {{ option.shortLabel }}@if (option.hasPendingEdits) {<span class="text-blue-500 dark:text-blue-400">*</span>}
                                </span>
                            </span>
                        </ng-template>
                    </p-selectButton>
                }
            </div>

            <div class="flex flex-wrap items-end gap-3">
                <span class="flex w-64 shrink-0 flex-col gap-1">
                    <label class="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {{ 'entity-translations.source' | translate }}
                    </label>
                    <phx-select
                        class="w-full"
                        size="small"
                        dropdownType="EntityTranslationsSourceLanguage"
                        [items]="sourceOptions()"
                        optionLabel="label"
                        optionValue="code"
                        [value]="sourceLanguage()"
                        (onModelChange)="sourceLanguage.set($event)"
                        [showClear]="false"
                        [searchable]="false"
                        [itemTemplate]="sourceOptionTemplate"
                        [dataTestId]="'entity-translations-source-language'"
                    />
                </span>

                <p-button
                    aiFeature
                    size="small"
                    severity="secondary"
                    [outlined]="true"
                    styleClass="hover:!bg-gray-200 dark:hover:!bg-gray-700"
                    [disabled]="!canTranslateAllLanguages()"
                    [loading]="isTranslatingAllLanguages()"
                    icon="fa-solid fa-wand-magic-sparkles"
                    [label]="'entity-translations.translate-all-languages' | translate"
                    (onClick)="onTranslateAllLanguages()"
                    data-testid="entity-translations-ai-all-languages"
                />
            </div>
        </div>

        <div class="mb-4 ml-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <p class="text-sm text-gray-500 dark:text-gray-400">
                <span class="text-blue-500 dark:text-blue-400">*</span>
                {{ 'entity-translations.unsaved-changes-hint' | translate }} &middot;
                {{ 'entity-translations.more-languages-hint' | translate }}
            </p>

            <phx-checkbox
                aiFeature
                [ngModel]="overwriteExisting()"
                (inlineChange)="overwriteExisting.set($event)"
                [disabled]="isTranslating()"
                [label]="'entity-translations.overwrite-existing' | translate"
                labelClass="text-sm text-gray-600 dark:text-gray-400"
                [dataTestId]="'entity-translations-overwrite'"
            />
        </div>

        <div class="mb-3.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <p class="ml-1 text-sm text-gray-600 dark:text-gray-400">
                {{
                    'entity-translations.coverage'
                        | translate: { translated: translatedFieldCount(), total: fieldViews().length }
                }}
            </p>

            <span class="flex flex-wrap items-center gap-2">
                <p-button
                    aiFeature
                    size="small"
                    severity="secondary"
                    [outlined]="true"
                    styleClass="hover:!bg-gray-200 dark:hover:!bg-gray-700"
                    [disabled]="!canTranslateAll()"
                    [loading]="isTranslatingAll()"
                    icon="fa-solid fa-wand-magic-sparkles"
                    [label]="'entity-translations.translate-all' | translate: { language: targetTag() }"
                    (onClick)="onTranslateAll()"
                    data-testid="entity-translations-ai-all"
                />

                @if (canRemoveLanguage() || isMarkedForRemoval()) {
                    <p-button
                        size="small"
                        [severity]="isMarkedForRemoval() ? 'secondary' : 'danger'"
                        [outlined]="true"
                        [icon]="isMarkedForRemoval() ? 'fa-solid fa-rotate-left' : 'fa-solid fa-trash'"
                        [label]="(isMarkedForRemoval() ? 'entity-translations.undo-removal' : 'entity-translations.remove-language') | translate: { language: targetTag() }"
                        [pTooltip]="'entity-translations.remove-language-hint' | translate"
                        (onClick)="onToggleRemoveLanguage()"
                        data-testid="entity-translations-remove-language"
                    />
                }
            </span>
        </div>

        <div
            class="overflow-hidden rounded-xl border border-gray-300 dark:border-gray-700"
            [ngClass]="isMarkedForRemoval() ? 'pointer-events-none opacity-50' : ''"
        >
            @for (view of fieldViews(); track view.field) {
                <div
                    class="grid grid-cols-[150px_1fr] items-start gap-5 border-t border-gray-200 px-4 py-3.5 first:border-t-0 dark:border-gray-700"
                >
                    <div class="pt-1.5 text-sm text-gray-600 dark:text-gray-400">{{ view.label | translate }}</div>
                    <!-- Both panes are flex columns so the taller one sets the row height and the shorter
                         one grows into it - the value box and the textarea carry `grow`. The action row is
                         a grid row of its own, below the matched pair. -->
                    <div class="grid gap-x-3.5 md:grid-cols-2">
                        <div class="flex flex-col">
                            <div class="mb-1.5 flex items-center gap-2">
                                <p-tag severity="secondary" styleClass="text-[11px] tracking-wider" [value]="sourceTag()" />
                                <span class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ 'entity-translations.source-pane' | translate }}
                                </span>
                            </div>
                            <div
                                class="grow whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800"
                                [ngClass]="{
                                    'min-h-[7.5rem]': view.startsTall,
                                    'text-gray-600 dark:text-gray-400': view.sourceValue,
                                    'italic text-gray-400 dark:text-gray-500': !view.sourceValue
                                }"
                            >
                                {{ view.sourceValue || ('entity-translations.source-empty' | translate) }}
                            </div>
                        </div>
                        <div class="mt-3.5 flex flex-col md:mt-0">
                            <div class="mb-1.5 flex items-center gap-2">
                                <p-tag severity="secondary" styleClass="text-[11px] tracking-wider" [value]="targetTag()" />
                                <span class="text-xs text-gray-500 dark:text-gray-400">
                                    {{ 'entity-translations.target-pane' | translate }}
                                </span>
                                @if (view.tag) {
                                    <p-tag
                                        severity="secondary"
                                        styleClass="text-xs"
                                        [icon]="view.tag.icon"
                                        [value]="view.tag.label"
                                        [pTooltip]="view.tag.hint"
                                        [attr.data-testid]="'entity-translations-state-' + view.field"
                                    />
                                }
                            </div>
                            <ng-container *ngTemplateOutlet="targetInput; context: { $implicit: view }" />
                        </div>

                        <div class="md:col-start-2">
                            <ng-container *ngTemplateOutlet="targetActions; context: { $implicit: view }" />
                        </div>
                    </div>
                </div>
            }
        </div>

        <div class="mt-3.5 ml-1 min-h-4 text-xs">
            @if (isMarkedForRemoval()) {
                <p
                    class="flex items-center gap-2 font-semibold text-amber-600 dark:text-amber-400"
                    data-testid="entity-translations-removal-pending"
                >
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    {{ 'entity-translations.removal-pending' | translate: { language: targetTag() } }}
                </p>
            } @else {
                <p class="text-gray-600 dark:text-gray-400">{{ 'entity-translations.fallback-hint' | translate }}</p>
            }
        </div>
    </div>
}

<ng-template #languageOptionTemplate let-option>
    <span class="flex w-full items-center gap-2">
        <span class="h-2 w-2 shrink-0 rounded-full" [ngClass]="coverageDotClass(option.coverage)"></span>
        <span class="whitespace-nowrap">
            {{ option.label }}@if (option.hasPendingEdits) {<span class="text-blue-500 dark:text-blue-400">*</span>}
        </span>
        <span class="ml-auto text-xs text-gray-500 dark:text-gray-400">{{ option.coverageLabel }}</span>
    </span>
</ng-template>

<ng-template #sourceOptionTemplate let-option>
    <span class="flex w-full items-center gap-3">
        {{ option.label }}
        <span class="ml-auto text-xs text-gray-500 dark:text-gray-400">{{ option.hint }}</span>
    </span>
</ng-template>

<!-- Input and actions are separate templates so that they can sit in different grid rows: only the
     input takes part in the height matching with the source pane, the buttons hang below it. -->
<ng-template #targetInput let-view>
    <div class="relative flex grow flex-col">
        <textarea
            pTextarea
            [autoResize]="true"
            rows="1"
            class="w-full grow resize-none !pr-9 placeholder:!text-gray-400 dark:placeholder:!text-gray-500"
            [ngClass]="view.startsTall ? 'min-h-[7.5rem]' : ''"
            [ngModel]="view.editedValue"
            [ngModelOptions]="{ standalone: true }"
            [disabled]="isMarkedForRemoval()"
            (ngModelChange)="onValueChange(view, $event)"
            [placeholder]="'entity-translations.not-translated' | translate"
            [attr.data-testid]="'entity-translations-value-' + view.field"
        ></textarea>

        @if (view.editedValue) {
            <button
                type="button"
                class="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                [pTooltip]="'entity-translations.clear-field' | translate"
                [attr.aria-label]="'entity-translations.clear-field' | translate"
                (click)="onValueChange(view, '')"
                [attr.data-testid]="'entity-translations-clear-' + view.field"
            >
                <i class="fa-solid fa-xmark"></i>
            </button>
        }
    </div>
</ng-template>

<ng-template #targetActions let-view>
    <div class="mt-1.5 flex flex-wrap items-center gap-2">
        @if (view.isUnsaved) {
            <p-tag
                severity="warn"
                styleClass="text-xs"
                icon="fa-regular fa-floppy-disk"
                [value]="'entity-translations.unsaved' | translate"
                [pTooltip]="'entity-translations.unsaved-hint' | translate"
                [attr.data-testid]="'entity-translations-unsaved-' + view.field"
            />
        }

        <p-button
            class="ml-auto"
            size="small"
            severity="secondary"
            [outlined]="true"
            styleClass="hover:!bg-gray-200 dark:hover:!bg-gray-700"
            [disabled]="!view.sourceValue || sourceLanguage() === targetLanguage()"
            [label]="'entity-translations.copy-from-source' | translate: { language: sourceTag() }"
            (onClick)="onCopyFromSource(view)"
            [attr.data-testid]="'entity-translations-copy-' + view.field"
        />

        <p-button
            aiFeature
            size="small"
            severity="secondary"
            [outlined]="true"
            styleClass="hover:!bg-gray-200 dark:hover:!bg-gray-700"
            [disabled]="!view.sourceValue || sourceLanguage() === targetLanguage() || isTranslating()"
            [loading]="isFieldTranslating(view.field)"
            icon="fa-solid fa-wand-magic-sparkles"
            [label]="'entity-translations.translate-field' | translate: { language: targetTag() }"
            (onClick)="onTranslateField(view)"
            [attr.data-testid]="'entity-translations-ai-' + view.field"
        />
    </div>
</ng-template>
